import type { IconName } from '../ui/icons.js';
import type { CurveMode, GradientMode } from './fields/values.js';

/**
 * Types for the Inspector's typed component forms. Kept apart from
 * component-schemas.ts so the per-system form files in ./forms/ can import
 * them without an import cycle.
 */

export type FieldType =
  | { kind: 'number'; step?: number; min?: number; max?: number; integer?: boolean }
  | { kind: 'bool' }
  | { kind: 'string'; placeholder?: string }
  /**
   * Project file reference, edited with an AssetField (thumbnail, picker, drag
   * and drop). `prefixes` are stored before matching files (`{ '.glb': 'gltf:' }`),
   * `builtins` are non-file values offered by the picker (`primitive:cube`),
   * `label` names the type ("Mesh"; default from the suffix).
   */
  | {
      kind: 'path';
      suffix: string | readonly string[];
      prefix?: string;
      allowObject?: boolean;
      prefixes?: Readonly<Record<string, string>>;
      builtins?: readonly string[];
      label?: string;
    }
  | { kind: 'enum'; options: readonly string[] }
  | { kind: 'color'; alpha?: boolean }
  | { kind: 'vec3'; step?: number }
  | { kind: 'tags' }
  /** One of the project's `settings.layers`. */
  | { kind: 'layer' }
  /** `[bool, bool, bool]` per axis. */
  | { kind: 'axes' }
  | { kind: 'json' }
  /** Material shader: standard / toon / unlit or a project `*.wgsl` file. */
  | { kind: 'shader' }
  /** Nested object edited with its own typed fields (absent keys = defaults). */
  | { kind: 'object'; fields: FieldDef[] }
  /** `settings.input.actions` (name → codes) or, with `axes`, `settings.input.axes`. */
  | { kind: 'inputBindings'; axes?: boolean }
  /**
   * MinMaxCurve (number, `[min, max]`, `{ curve, multiplier }`, `{ curves: [a, b] }`) edited
   * with a graph; `modes` limits the choices, `plain` edits bare curve keys `[[t, v], ...]`.
   */
  | { kind: 'curve'; step?: number; min?: number; max?: number; modes?: readonly CurveMode[]; plain?: boolean }
  /**
   * MinMaxGradient (color, `{ random }`, `{ gradient }`, `{ gradients }`) edited with a
   * gradient bar; `plain` edits a bare `{ colorKeys, alphaKeys, mode }` gradient.
   */
  | { kind: 'gradient'; modes?: readonly GradientMode[]; plain?: boolean };

export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  /** Hover hint. */
  hint?: string;
  /** Only shown when this returns true for the component's current value. */
  showIf?: (value: Record<string, unknown>) => boolean;
  /** Value shown when the key is absent (derived from other fields). */
  read?: (value: Record<string, unknown>) => unknown;
  /** Keys removed whenever this field is set (legacy fields it replaces). */
  clears?: string[];
}

export interface ComponentDef {
  key: string;
  label: string;
  icon: IconName;
  /** Inserted by "Add Component". */
  defaults: unknown;
  fields: FieldDef[];
  /** The value is not an object (e.g. tags is an array). */
  array?: boolean;
  /** The value is a single scalar edited with this field type (e.g. `layer`). */
  scalar?: FieldType;
}

/**
 * One form file in ./forms/: a `default` export of one component definition
 * or several. `order` places it in the Add Component list (lower first;
 * built-in components use 0-100 in steps of 10). A form whose `key` matches
 * a built-in component replaces that component's form.
 */
export type ComponentForm = ComponentDef & { order?: number };
export type FormModule = { default: ComponentForm | ComponentForm[] };

export const num = (step = 0.1, extra: Partial<{ min: number; max: number; integer: boolean }> = {}) =>
  ({ kind: 'number', step, ...extra }) as const;
